import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/legal';

/**
 * Philosophie - Du-Ansprache, Fokus auf Absicherung (§34d)
 */

// Controlled Quote Block
const Quote = ({ children, author }) => (
  <motion.blockquote
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.5 }}
    className="py-12 sm:py-16"
  >
    <div className="max-w-xl mx-auto px-6 md:px-8">
      <p className="font-playfair text-lg sm:text-xl text-white/80 leading-relaxed italic">
        „{children}"
      </p>
      {author && (
        <p className="mt-4 text-sm text-cobalt/60">
          – {author}
        </p>
      )}
    </div>
  </motion.blockquote>
);

// Chapter Section
const Chapter = ({ label, children }) => (
  <section className="py-12 sm:py-16">
    <div className="max-w-2xl mx-auto px-6 md:px-8">
      {label && (
        <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-white/30 mb-6">
          {label}
        </p>
      )}
      {children}
    </div>
  </section>
);

const Philosophie = () => {
  const navigate = useNavigate();

  const fadeIn = {
    initial: { opacity: 0, y: 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-80px' },
    transition: { duration: 0.5, ease: 'easeOut' }
  };

  return (
    <div className="min-h-screen bg-navy font-manrope">
      <Navigation variant="dark" />

      {/* HERO */}
      <section className="pt-32 sm:pt-40 md:pt-48 pb-16 sm:pb-20">
        <div className="max-w-2xl mx-auto px-6 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-cobalt text-xs font-medium uppercase tracking-[0.2em] mb-4">
              Philosophie
            </p>
            <h1 className="font-playfair text-3xl sm:text-4xl md:text-5xl font-semibold text-white leading-[1.2] tracking-tight mb-6">
              Warum Absicherung<br />
              kein Nachgedanke sein darf
            </h1>
            <p className="text-base sm:text-lg text-white/50 leading-relaxed max-w-lg">
              Gedanken über finanzielle Entscheidungen, Struktur und den 
              Unterschied zwischen Zufall und Strategie.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CHAPTER: INTRO */}
      <Chapter>
        <motion.div {...fadeIn} className="space-y-6">
          <p className="text-lg text-white/80 leading-relaxed">
            Die meisten Menschen verdrängen das Thema Absicherung.
          </p>
          <p className="text-base text-white/55 leading-[1.8]">
            Nicht aus Unwissenheit. Sondern weil es unbequem ist. Weil es 
            Szenarien betrifft, die niemand erleben möchte. Krankheit, Unfall, 
            Berufsunfähigkeit. Dinge, die "den anderen" passieren.
          </p>
          <p className="font-playfair text-xl text-white/80 leading-relaxed pt-2">
            Bis sie plötzlich einem selbst passieren.
          </p>
          <p className="text-base text-white/55 leading-[1.8]">
            Und dann stellt sich heraus: Die Absicherung reicht nicht. 
            Oder sie existiert gar nicht.
          </p>
        </motion.div>
      </Chapter>

      {/* CHAPTER: STRUCTURE */}
      <Chapter label="Struktur">
        <motion.div {...fadeIn} className="space-y-6 text-base text-white/55 leading-[1.8]">
          <p>
            Ich habe im Laufe der Zeit immer wieder gesehen, wie Menschen 
            an diesem Punkt stehen. Nicht weil sie schlechte Entscheidungen 
            getroffen haben. Sondern weil sie gar keine getroffen haben.
          </p>
          <p>
            "Ich kümmere mich später darum." "Ich bin ja noch jung." 
            "Mir passiert schon nichts."
          </p>
          <p className="text-white/70">
            Und genau das ist das Problem.
          </p>
        </motion.div>
      </Chapter>

      <Quote>
        Die beste Zeit, sich abzusichern, war vor zehn Jahren. 
        Die zweitbeste Zeit ist jetzt.
      </Quote>

      {/* CHAPTER: INDEPENDENCE */}
      <Chapter label="Unabhängigkeit">
        <motion.div {...fadeIn} className="space-y-6 text-base text-white/55 leading-[1.8]">
          <p className="text-white/70">
            Ich arbeite als Versicherungsmakler nach §34d GewO und werde durch die
            Industrie- und Handelskammer (IHK) reguliert und überwacht. 
          </p>
          <p>
            Das bedeutet nicht nur fachliche Anforderungen, sondern vor allem eines: 
            Eine klare Ausrichtung auf die Interessen meiner Kunden.
          </p>
          <p>
            Sowohl durch gesetzliche Rahmenbedingungen als auch aus eigener Überzeugung 
            steht nicht das Produkt im Mittelpunkt, sondern die Entscheidung, 
            die für dich langfristig sinnvoll ist.
          </p>
          <p>
            Ich habe keinen Vorteil darin, bestimmte Lösungen zu bevorzugen. 
            Und keinen Anreiz, unpassende Entscheidungen zu treffen.
          </p>
          <p className="text-white/70">
            Denn der eigentliche Maßstab ist nicht ein Abschluss. 
            Sondern das Ergebnis über viele Jahre.
          </p>
        </motion.div>
      </Chapter>

      {/* CHAPTER: ISOLATED DECISIONS */}
      <Chapter label="Isolierte Entscheidungen">
        <motion.div {...fadeIn} className="space-y-6 text-base text-white/55 leading-[1.8]">
          <p>
            In der Praxis zeigt sich häufig ein anderes Bild. Viele haben 
            irgendwann mal eine BU abgeschlossen, aber die Absicherungshöhe 
            passt längst nicht mehr zum aktuellen Einkommen.
          </p>
          <p>
            Andere haben eine Risikolebensversicherung, die beim Abschluss 
            sinnvoll war. Aber die Familiensituation hat sich verändert.
          </p>
          <p className="text-white/70">
            Das Problem?
          </p>
          <p>
            Die Entscheidungen wurden isoliert getroffen. Sie wurden nie 
            als Teil eines Gesamtkonzepts betrachtet. Und niemand hat sie 
            seitdem überprüft.
          </p>
        </motion.div>
      </Chapter>

      {/* CHAPTER: SYSTEM */}
      <Chapter label="Systemdenken">
        <motion.div {...fadeIn} className="space-y-6 text-base text-white/55 leading-[1.8]">
          <p className="text-white/70">
            Ich betrachte Absicherung nicht isoliert.
          </p>
          <p className="font-playfair text-xl text-white/80 leading-relaxed py-2">
            Sondern als System.
          </p>
          <p>
            Ein System, in dem jede Entscheidung Auswirkungen auf eine andere hat. 
            BU und Krankentagegeld greifen ineinander. Risikoleben und 
            Familienabsicherung hängen zusammen. Altersvorsorge baut auf einer 
            soliden Basis auf.
          </p>
          <p>
            Und genau deshalb geht es nicht darum, die "günstigste" Police zu finden. 
            Sondern die richtige Kombination.
          </p>
        </motion.div>
      </Chapter>

      <Quote author="Seneca">
        Das Glück ist auf der Seite des Vorbereiteten.
      </Quote>

      {/* CHAPTER: KNOWLEDGE */}
      <Chapter label="Wissen">
        <motion.div {...fadeIn} className="space-y-6 text-base text-white/55 leading-[1.8]">
          <p>
            Viele Menschen suchen nach der einen richtigen Versicherung.
          </p>
          <p className="text-white/70">
            Dabei liegt der größte Hebel oft im Verständnis.
          </p>
          <p>
            Zu verstehen, welche Risiken wirklich relevant sind. Zu wissen, 
            wie hoch die eigene Lücke tatsächlich ist. Und zu erkennen, 
            welche Absicherung Priorität hat.
          </p>
        </motion.div>
      </Chapter>

      {/* CHAPTER: CLARITY */}
      <Chapter label="Klarheit">
        <motion.div {...fadeIn} className="space-y-6 text-base text-white/55 leading-[1.8]">
          <p>
            Gute Entscheidungen entstehen nicht durch mehr Optionen.
          </p>
          <p className="text-white/70">
            Sondern durch Klarheit. Und durch einen klaren Rahmen.
          </p>
          <p>
            Wenn du weißt, wie groß deine Lücke ist, welche Risiken du 
            absichern solltest und in welcher Reihenfolge — dann werden 
            die Entscheidungen einfacher.
          </p>
        </motion.div>
      </Chapter>

      {/* CHAPTER: CLOSING */}
      <Chapter>
        <motion.div {...fadeIn} className="space-y-6 text-base text-white/55 leading-[1.8]">
          <p className="text-white/70">
            Mein Anspruch ist es, Struktur zu schaffen.
          </p>
          <p>
            Zusammenhänge sichtbar zu machen. Und Absicherungsentscheidungen so zu treffen, 
            dass sie langfristig tragen.
          </p>
          <p>
            Denn am Ende entscheidet nicht die einzelne Police.
          </p>
          <p className="font-playfair text-xl text-white/80 leading-relaxed pt-2">
            Sondern die Strategie dahinter.
          </p>
        </motion.div>
      </Chapter>

      {/* CTA */}
      <section className="py-20 sm:py-28">
        <div className="max-w-2xl mx-auto px-6 md:px-8">
          <motion.div {...fadeIn}>
            <div className="border-t border-white/10 pt-12 sm:pt-16">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-white/40 mb-4">
                Nächster Schritt
              </p>
              <h2 className="font-playfair text-2xl sm:text-3xl font-semibold text-white mb-4">
                Lass uns sprechen
              </h2>
              <p className="text-base text-white/50 mb-8 max-w-md">
                Finde heraus, wie deine Absicherung strukturiert werden kann — 
                und wo die größten Lücken liegen.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate('/strategiegespraech')}
                  className="group px-6 py-3 bg-cobalt text-white rounded-lg text-sm font-medium hover:bg-cobalt/90 transition-colors flex items-center justify-center gap-2"
                  data-testid="philosophie-booking-btn"
                >
                  Gespräch vereinbaren
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer variant="dark" />
    </div>
  );
};

export default Philosophie;
